/*
 * Recently viewed products.
 *
 * Privacy boundary: this script stores only product handles in this browser's
 * localStorage. Title, price, and image are fetched from Shopify at render
 * time, so a previously viewed product cannot show a stored, stale price.
 */
(() => {
  'use strict';

  const STORE_KEY = 'toreca:recently-viewed:v1';
  const LIMIT = 20;

  const uniqueHandles = (values, limit = LIMIT) => {
    const seen = new Set();
    const handles = [];
    (Array.isArray(values) ? values : []).forEach((value) => {
      const handle = typeof value === 'string' ? value.trim() : '';
      if (!handle || seen.has(handle) || handles.length >= limit) return;
      seen.add(handle);
      handles.push(handle);
    });
    return handles;
  };

  const readHandles = (storage) => {
    try {
      return uniqueHandles(JSON.parse(storage.getItem(STORE_KEY) || '[]'));
    } catch (error) {
      return [];
    }
  };

  const writeHandles = (storage, handles) => {
    try {
      storage.setItem(STORE_KEY, JSON.stringify(uniqueHandles(handles)));
      return true;
    } catch (error) {
      return false;
    }
  };

  const recordHandle = (storage, handle) => {
    const current = typeof handle === 'string' ? handle.trim() : '';
    if (!current) return [];
    const next = uniqueHandles([current].concat(readHandles(storage)));
    writeHandles(storage, next);
    return next;
  };

  const normaliseProduct = (product, handle) => {
    if (!product || typeof product !== 'object') return null;
    const title = typeof product.title === 'string' ? product.title.trim() : '';
    const url = typeof product.url === 'string' && product.url ? product.url : `/products/${encodeURIComponent(handle)}`;
    const image = typeof product.featured_image === 'string' ? product.featured_image : '';
    const price = Number(product.price);
    if (!title || !Number.isFinite(price) || price < 0) return null;
    return { title, url, image, price };
  };

  const formatPrice = (cents) => {
    const yen = Math.round(Number(cents) / 100);
    return `¥${yen.toLocaleString('ja-JP')}`;
  };

  const fetchProduct = (handle) =>
    window
      .fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => normaliseProduct(product, handle))
      .catch(() => null);

  const card = (product) => {
    const item = document.createElement('li');
    item.className = 'recently-viewed__card';

    const link = document.createElement('a');
    link.className = 'recently-viewed__link';
    link.href = product.url;

    if (product.image) {
      const media = document.createElement('span');
      media.className = 'recently-viewed__media';
      const image = document.createElement('img');
      image.className = 'recently-viewed__image';
      image.src = product.image;
      image.alt = '';
      image.loading = 'lazy';
      media.appendChild(image);
      link.appendChild(media);
    }

    const body = document.createElement('span');
    body.className = 'recently-viewed__body';
    const price = document.createElement('span');
    price.className = 'recently-viewed__price tnum';
    price.textContent = formatPrice(product.price);
    const title = document.createElement('span');
    title.className = 'recently-viewed__title';
    title.textContent = product.title;
    body.append(price, title);
    link.appendChild(body);
    item.appendChild(link);
    return item;
  };

  const initShelf = (shelf) => {
    if (shelf.dataset.recentlyViewedReady === 'true') return;
    shelf.dataset.recentlyViewedReady = 'true';

    let storage;
    try {
      storage = window.localStorage;
    } catch (error) {
      return;
    }

    const currentHandle = shelf.dataset.recentlyViewedCurrentHandle || '';
    if (currentHandle) recordHandle(storage, currentHandle);

    const handles = readHandles(storage).filter((handle) => handle !== currentHandle);
    const list = shelf.querySelector('[data-recently-viewed-list]');
    if (!list || !handles.length || !window.fetch) return;

    Promise.all(handles.map(fetchProduct)).then((products) => {
      const available = products.filter(Boolean);
      if (!available.length) return;
      list.replaceChildren(...available.map(card));
      shelf.hidden = false;
    });
  };

  const init = () => document.querySelectorAll('[data-recently-viewed]').forEach(initShelf);

  window.TorecaRecentlyViewed = { LIMIT, STORE_KEY, uniqueHandles, readHandles, writeHandles, recordHandle, normaliseProduct, formatPrice };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-recently-viewed]').forEach(initShelf);
  });
})();
